/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import type { EstimateLineItemForCalculation } from "@/types/estimate-calculator";
import type { LineItem } from "@/types/line-items";
import type { BudgetRates } from "./estimates-types";
import { calculateLineItemPricing } from "./estimates-calculations";
import { logger } from "@/lib/utils/logger";

export interface CostBrowserItemLike {
  id: string;
  name: string;
  type: "material" | "labor" | "equipment" | "subcontractor";
  unit: string;
  cost: number;
}

export const ESTIMATE_LINE_ITEM_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function createDefaultLineItem(): EstimateLineItemForCalculation {
  return {
    item_name: "",
    description: "",
    quantity: 1,
    unit_cost: 0,
    total_cost: 0,
    unit: "ea",
    material_id: null,
    labor_task_id: null,
    labor_class_id: null,
    equipment_id: null,
    category_id: null,
    cost_profile_id: null,
    preferred_supplier_id: null,
    tax_code: null,
    is_tax_exempt: false,
    item_type: null,
    sort_order: null,
    created_at: null,
    updated_at: null,
  };
}

export function isEstimateLineItemId(value: string): boolean {
  return ESTIMATE_LINE_ITEM_ID_PATTERN.test(value);
}

export function mapCostBrowserItemToLineItem(
  item: CostBrowserItemLike,
  workAreaId: string,
  budgetRates?: BudgetRates | null,
  _budgetId?: string | null,
  estimateSubtotalForOverheadAllocation?: number,
): LineItem {
  const quantity = 1;
  const unitCost = item.cost;
  const totalCost = item.cost;
  const materialCost = item.type === "material" ? item.cost : 0;
  const laborCost = item.type === "labor" ? item.cost : 0;
  const equipmentCost = item.type === "equipment" ? item.cost : 0;
  const subcontractorCost = item.type === "subcontractor" ? item.cost : 0;

  const pricing = calculateLineItemPricing(
    unitCost,
    totalCost,
    materialCost,
    laborCost,
    equipmentCost,
    budgetRates,
    undefined,
    undefined,
    undefined,
    estimateSubtotalForOverheadAllocation,
    subcontractorCost,
  );

  if (budgetRates) {
    logger.info("Calculating line item pricing with budget rates", {
      overheadRate: budgetRates.overheadRate,
      marginRate: budgetRates.marginRate,
      overheadRecoveryMethod: budgetRates.overheadRecoveryMethod,
      categoryOverheadRates: budgetRates.categoryOverheadRates,
      calculatedProfitPercentage: pricing.profitPercentage,
      unitCost,
      totalCost,
    });
  } else {
    logger.warn("No budget rates available, using defaults", {
      itemType: item.type,
      itemCost: item.cost,
    });
  }

  return {
    id: `${item.type}-${item.id}-${Date.now()}`,
    description: item.name,
    quantity,
    unit: item.unit,
    unitCost,
    totalCost,
    materialCost,
    laborCost,
    equipmentCost,
    subcontractorCost,
    unitPrice: pricing.unitPrice,
    totalPrice: pricing.totalPrice,
    marginPercentage: pricing.marginPercentage,
    workAreaId,
    profitPercentage: pricing.profitPercentage,
    material_id: item.type === "material" ? item.id : undefined,
    labor_task_id: item.type === "labor" ? item.id : undefined,
    equipment_id: item.type === "equipment" ? item.id : undefined,
    subcontractor_id: item.type === "subcontractor" ? item.id : undefined,
  };
}
